import React, { useCallback, useMemo } from 'react'
import { makeStyles } from '@material-ui/styles'
import {
  PatientSideBarChatIcon,
  PatientSideBarLiveCallsIcon,
  PatientSideBarNotesIcon,
  CopilotSidebarIcon
} from 'components/common/icons'
import { OpenClaritySidebarIcon } from 'components/common/icons'
import SidebarItem from './SidebarItem'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { PatientSidebarItemKeys } from './patientSidebarConsts'
import { trackEvent } from 'utils/analyticsUtils'
import { setActiveItem } from './patientSidebarSlice'
import useFeatureFlags from 'hooks/useFeatureFlags'
import usePlanLimits from 'hooks/usePlanLimits'
import useSelectPatientView from '../useSelectPatientView'
import NewerAppVersionRequired from './NewerAppVersionRequired'

const useStyles = makeStyles(theme => ({
  patientSidebarRoot: {
    position: 'absolute',
    top: 0,
    right: 0,
    display: 'flex',
    flexDirection: 'column',
    borderLeft: '1px solid #e6e6e6',
    borderTop: '1px solid #e6e6e6',
    width: 'var(--patients-side-bar-collapsed-width)',
    zIndex: 999,
    height: '100%',
    background: 'white',
    transition: 'all 0.15s ease-in-out',
    overflow: 'hidden',
    '&:hover': {
      width: 160
    }
  }
}))

const PatientSidebar = () => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const classes = useStyles()

  const { selectPatientView } = useSelectPatientView()
  const { liveCalls: liveCallsFF, copilotControl: copilotFF } = useFeatureFlags()
  const { isHI } = usePlanLimits()

  const { activeItem } = useSelector(state => state.patientSidebarReducer)
  const { patient } = useSelector(state => state.patientsReducer)

  const patientFFs = useMemo(() => JSON.parse(patient?.user?.featureFlags?.flags || '{}'), [patient])

  const ocpId = useMemo(() => {
    const externalIds = patient?.user?.externalIds
    if (!externalIds) {
      return undefined
    }

    if (typeof externalIds === 'string') {
      try {
        return JSON.parse(externalIds)?.ocp
      } catch (e) {
        return undefined
      }
    }

    if (typeof externalIds === 'object') {
      return externalIds.ocp
    }

    return undefined
  }, [patient])

  const appconfig = useSelector(state => state.appReducer.appconfig)

  const clarityUrl = useMemo(() => {
    if (!ocpId) {
      return null
    }
    const template = appconfig?.app?.integrations?.solventum?.clarityPortalUrlTemplate
    if (!template) {
      return null
    }
    return template.replace('{ClarityPortalPatientID}', encodeURIComponent(ocpId))
  }, [appconfig, ocpId])

  const items = useMemo(
    () =>
      [
        {
          key: PatientSidebarItemKeys.Copilot,
          title: t('pages.patients.patientSidebar.items.copilot.title'),
          icon: CopilotSidebarIcon,
          isDisabled: false,
          condition: () => isHI && copilotFF
        },
        {
          key: PatientSidebarItemKeys.Chat,
          title: t('pages.patients.patientSidebar.items.chat.title'),
          icon: PatientSideBarChatIcon,
          isDisabled: false,
          condition: () => true
        },
        {
          key: PatientSidebarItemKeys.Notes,
          title: t('pages.patients.patientSidebar.items.notes.title'),
          icon: PatientSideBarNotesIcon,
          isDisabled: false,
          condition: () => true
        },
        {
          key: PatientSidebarItemKeys.LiveCalls,
          title: t('pages.patients.patientSidebar.items.liveCalls.title'),
          icon: PatientSideBarLiveCallsIcon,
          condition: () => liveCallsFF,
          isDisabled: !patientFFs.liveCallsAvailable,
          disabledHoverComponent: NewerAppVersionRequired
        },
        {
          key: PatientSidebarItemKeys.OpenClarity,
          title: t('pages.patients.patientSidebar.items.openClarity.title', 'Open in Clarity'),
          icon: OpenClaritySidebarIcon,
          isDisabled: false,
          condition: () => !!clarityUrl
        }
      ].filter(item => item.condition()),
    [liveCallsFF, patientFFs.liveCallsAvailable, t, copilotFF, clarityUrl, isHI]
  )

  const handleItemClicked = useCallback(
    itemKey => {
      if (itemKey === PatientSidebarItemKeys.OpenClarity) {
        trackEvent('Patient side bar - Open in Clarity clicked', { ocpId })
        if (clarityUrl) {
          window.open(clarityUrl, '_blank', 'noopener,noreferrer')
        }
        return
      }
      trackEvent('Patient side bar - Item clicked', { itemKey })
      selectPatientView(itemKey)
      dispatch(setActiveItem(itemKey))
    },
    [dispatch, selectPatientView, clarityUrl, ocpId]
  )

  return (
    <div className={classes.patientSidebarRoot} id="patient-sidebar">
      {items.map(item => (
        <SidebarItem
          id={`patient-side-bar-id-${item.key}`}
          key={`patient-side-bar-id-${item.key}`}
          icon={item.icon}
          title={item.title}
          isDisabled={item.isDisabled}
          disabledHoverComponent={item.disabledHoverComponent}
          isActive={item.key === activeItem}
          onClick={() => handleItemClicked(item.key)}
        />
      ))}
    </div>
  )
}

export default PatientSidebar
